import { memo } from "react";
import { useTranslation } from "react-i18next";
import LoginButton from "../login-button/LoginButton";

type Provider = "Google" | "Microsoft" | "Apple";

interface SocialLoginButtonsProps {
  onProvider: (provider: Provider) => void;
  loading?: boolean;
  /**
   * Where the divider sits relative to the buttons. Sign-in puts the
   * providers under the password form ("or continue with"); sign-up leads
   * with them and the divider introduces the email form below.
   */
  divider?: "above" | "below";
  /** The divider text; defaults to "or continue with". */
  dividerLabel?: string;
}

/**
 * The Google / Microsoft / Apple buttons plus their divider. Shared by the
 * sign-in and sign-up flows so both stay in sync.
 */
const SocialLoginButtons = memo(
  ({ onProvider, loading = false, divider = "above", dividerLabel }: SocialLoginButtonsProps) => {
    const { t } = useTranslation();
    const label = (dividerLabel ?? `${t("auth.or")} ${t("auth.continueWith")}`).toUpperCase();

    const dividerRow = (
      <div className="w-full flex items-center mt-4 mb-4">
        <hr className="flex-grow" />
        <p className="mx-2 text-sm">{label}</p>
        <hr className="flex-grow" />
      </div>
    );

    return (
      <div className="w-full">
        {divider === "above" && dividerRow}
        <div
          className={`w-full grid grid-cols-1 gap-3 sm:grid-cols-3 ${
            loading ? "pointer-events-none" : "pointer-events-auto"
          }`}
        >
          <LoginButton
            text="Google"
            icon="/assets/images/icons/google-icon.svg"
            onClick={() => onProvider("Google")}
            disabled={loading}
          />
          <LoginButton
            text="Microsoft"
            icon="/assets/images/icons/microsoft-icon.svg"
            onClick={() => onProvider("Microsoft")}
            disabled={loading}
          />
          <LoginButton
            text="Apple"
            icon="/assets/images/icons/apple-icon.svg"
            onClick={() => onProvider("Apple")}
            disabled={loading}
            iconClassName="dark:invert"
          />
        </div>
        {divider === "below" && dividerRow}
      </div>
    );
  }
);

export default SocialLoginButtons;
